import pseudoLocalization from 'pseudo-localization';
import { getAssetSchemas } from '../asset';
import { getSystemTypes } from '../system';
import { Property } from '../types/property.types';

/**
 * Retrieves translation entries from system types and asset schemas.
 * @internal
 * @returns {Promise<[string, string][]>} A promise that resolves to an array of translation entries.
 */
export const getTranslationEntries = async (): Promise<[string, string][]> => {
	const entries = new Map<string, string>();
	const systemTypes = await getSystemTypes();

	for (const systemType of systemTypes) {
		entries.set(systemType.displayName, systemType.displayName);

		const assetTypes = await getAssetSchemas({
			systemTypeId: systemType.id,
		});

		for (const assetType of assetTypes) {
			entries.set(assetType.displayName, assetType.displayName);
			entries.set(assetType.pluralName, assetType.pluralName);

			addPropertyEntries(assetType.getProperties(), entries);
		}
	}

	return Array.from(entries.entries());
};

const addPropertyEntries = (
	properties: Property[],
	entries: Map<string, string>,
) => {
	for (const property of properties) {
		if (entries.has(property.id)) {
			continue;
		}

		const { displayName } = property;

		if (!displayName) {
			continue;
		}

		entries.set(displayName, displayName);
	}
};

/**
 * Retrieves the translation for a given language.
 * @param {string} language - The language code.
 * @returns {Promise<Record<string, string>>} A promise that resolves to the translation object.
 */
export const getTranslation = async (
	language: string,
): Promise<Record<string, string>> => {
	const translation = await getTranslationEntries();
	if (language === 'en-PS') {
		return Object.fromEntries(
			translation.map(([key, value]) => [
				key,
				`[${pseudoLocalization.localize(value)}]`,
			]),
		);
	}
	return Object.fromEntries(translation);
};

/**
 * Retrieves the translation file for a given language. For usage with stylovyze
 * @param {string} language - The language code.
 * @returns {Promise<{ default: Record<string, string> }>} A promise that resolves to an object containing the translation.
 */
export const getTranslationFile = async (
	language: string,
): Promise<{
	default: Record<string, string>;
}> => {
	const translation = await getTranslation(language);
	return { default: translation };
};
